import { useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Calendar, Phone, MessageCircle } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';
import { getHubSpotMeetingUrl } from '@/lib/hubspot';

interface MeetingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onWhatsAppClick: () => void;
  unitNumber?: string;
}

export const MeetingModal = ({ open, onOpenChange, onWhatsAppClick, unitNumber }: MeetingModalProps) => {
  const meetingUrl = getHubSpotMeetingUrl();
  
  // Track consultation booking with unit context
  useEffect(() => {
    if (open && unitNumber) {
      trackEvent('residences_unit_consult_booked', { unit_number: unitNumber });
    }
  }, [open, unitNumber]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Book Your Consultation</DialogTitle>
          <DialogDescription>
            {unitNumber 
              ? `Schedule a 15-minute call with our team to discuss unit ${unitNumber}, pricing, and the purchase process.`
              : 'Schedule a 15-minute call with our team to discuss residence options, pricing, and the purchase process.'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* HubSpot Meetings Embed */}
          {meetingUrl ? (
            <div className="w-full rounded-lg overflow-hidden border border-border">
              <iframe
                src={meetingUrl}
                width="100%"
                height="600"
                frameBorder="0"
                className="w-full"
                title="HubSpot Meeting Scheduler"
                sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
              />
            </div>
          ) : (
            <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-lg p-8 text-center border-2 border-dashed border-primary/30">
              <Calendar className="w-16 h-16 text-primary mx-auto mb-4" />
              <p className="font-display text-lg font-semibold text-foreground mb-2">
                Meeting Scheduler
              </p>
              <p className="text-sm text-muted-foreground mb-4">
                Meeting scheduler is not configured. Please set VITE_HUBSPOT_MEETING_LINK in your environment variables.
              </p>
              <div className="inline-flex items-center gap-2 text-xs text-primary bg-primary/10 px-3 py-1.5 rounded-full">
                <Phone className="w-3 h-3" />
                <span>Not Configured</span>
              </div>
            </div>
          )}

          {/* Alternative Contact */}
          <div className="border-t pt-6">
            <p className="text-sm text-muted-foreground mb-4 text-center">
              Prefer to connect instantly?
            </p>
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  trackEvent('call_booked', { method: 'phone' });
                  window.location.href = 'tel:+13451234567';
                }}
              >
                <Phone className="w-4 h-4 mr-2" />
                Call Now
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  trackEvent('whatsapp_click', { location: 'meeting_modal' });
                  onWhatsAppClick();
                  onOpenChange(false);
                }}
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp
              </Button>
            </div>
          </div>

          {/* Info */}
          <p className="text-xs text-muted-foreground text-center">
            Our team is available Monday-Friday, 9am-5pm EST. Weekend appointments available by request.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

